/**
 * Add a contact.
 *
 * The client comes from the prop and is never a form field, matching the API —
 * the client is in the path there, so a request cannot name one client in the
 * URL and another in the body.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ApiError,
  Button,
  Input,
  Modal,
  Select,
  Switch,
  Textarea,
  api,
  toast,
} from '@connect/web-shared';

const schema = z.object({
  given_name: z.string().trim().min(1, 'A first name is needed.').max(160),
  surname: z.string().trim().max(160).optional(),
  job_title: z.string().trim().max(160).optional(),
  email: z.union([z.literal(''), z.email('That does not look like an email address.')]).optional(),
  phone: z.string().trim().max(32).optional(),
  role: z.enum(['primary', 'billing', 'technical', 'other']),
  is_active: z.boolean(),
  notes: z.string().trim().optional(),
});

type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = {
  given_name: '',
  surname: '',
  job_title: '',
  email: '',
  phone: '',
  role: 'other',
  is_active: true,
  notes: '',
};

const ROLE_OPTIONS = [
  { value: 'primary', label: 'Primary contact' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
];

export function ContactFormDialog({
  open,
  onClose,
  onCreated,
  clientPublicId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (publicId: string) => void;
  clientPublicId: string;
}) {
  const queryClient = useQueryClient();
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  useEffect(() => {
    if (open) form.reset(DEFAULTS);
  }, [open, form]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ public_id: string; full_name: string }>(
        `/crm/clients/${clientPublicId}/contacts`,
        Object.fromEntries(
          Object.entries(values).map(([key, value]) => [
            key,
            typeof value === 'string' && value.trim() === '' ? null : value,
          ]),
        ),
      ),
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] });
      // The client's contact count is a column on the clients register, so it
      // is stale the moment a contact is added.
      void queryClient.invalidateQueries({ queryKey: ['crm', 'clients'] });
      toast.success(`${record.full_name} added`);
      onCreated?.(record.public_id);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        // The single-primary-contact refusal names the person already holding
        // it, and has no field to attach to.
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New contact"
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={save.isPending}
            onClick={() => void form.handleSubmit((values) => save.mutateAsync(values))()}
          >
            Add contact
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit((values) => save.mutateAsync(values))();
        }}
      >
        {rootError ? (
          <p
            className="rounded-card border border-danger/25 bg-danger-subtle p-2.5 text-sm text-danger"
            role="alert"
          >
            {rootError}
          </p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            required
            error={form.formState.errors.given_name?.message}
            {...form.register('given_name')}
          />
          <Input
            label="Surname"
            hint="Plenty of people have one name."
            error={form.formState.errors.surname?.message}
            {...form.register('surname')}
          />
          <Input
            label="Job title"
            containerClassName="sm:col-span-2"
            error={form.formState.errors.job_title?.message}
            {...form.register('job_title')}
          />
          <Input
            label="Email"
            type="email"
            error={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <Input
            label="Phone"
            error={form.formState.errors.phone?.message}
            {...form.register('phone')}
          />
          <Select
            label="Role"
            options={ROLE_OPTIONS}
            hint="A client has at most one primary contact."
            error={form.formState.errors.role?.message}
            {...form.register('role')}
          />
        </div>

        <Switch label="Active" {...form.register('is_active')} />
        <Textarea label="Notes" rows={2} {...form.register('notes')} />
      </form>
    </Modal>
  );
}
